import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getChatsUnreadCount } from '../Reducers/actions/chatActions';
import { fetchApi } from '../../helpers/fetch';
import useAdaptivePolling from './useAdaptivePolling';

const useUnreadCountPolling = () => {
  const dispatch = useDispatch();
  const lastCountRef = useRef(null);
  const currentUnreadCount = useSelector((state) => state.chat.unread_chats_count);
  const pollingRef = useRef(null);

  // Helper function to compare unread counts
  const hasUnreadCountChanged = (newData, oldCount) => {
    if (oldCount === null) return true; // First time, always update
    if (!newData) return false;
    
    const newCount = newData.total_unread || 0;
    return newCount !== oldCount;
  };

  const pollForUnreadCount = async () => {
    // Fetch data directly without dispatching to compare first
    const data = await fetchApi('/api/chats/unread-count');
    
    if (!data.success) {
      throw new Error(`API request failed: ${data.message || 'Unknown error'}`);
    }
    
    const newUnreadData = data.data || {};
    const newCount = newUnreadData.total_unread || 0;
    const hasChanges = hasUnreadCountChanged(newUnreadData, lastCountRef.current);
    
    if (hasChanges) {
      const oldCount = lastCountRef.current;
      
      // Trigger fast polling if unread count increased (new messages received)
      if (newCount > oldCount && oldCount !== null) {
        // Trigger fast polling when unread count increases
        if (pollingRef.current?.triggerFastPoll) {
          pollingRef.current.triggerFastPoll();
        }
      }
      
      // Dispatch the action to update Redux store
      const result = await dispatch(getChatsUnreadCount());
      
      if (result && result.success) {
        lastCountRef.current = newCount;
      }
    }
  };

  // Initialize lastCountRef with current Redux state
  if (currentUnreadCount !== undefined && lastCountRef.current === null) {
    lastCountRef.current = currentUnreadCount;
  }

  // Set up adaptive polling - runs on all pages since header is always visible
  const adaptivePolling = useAdaptivePolling({
    pollingFunction: pollForUnreadCount,
    enabledRoutes: [], // No route restrictions - header is always present
    context: 'unread-count'
  });

  // Store polling reference for triggering fast polling from within pollForUnreadCount
  pollingRef.current = adaptivePolling;

  // Return utility functions for manual operations
  return {
    ...adaptivePolling,
    pollNow: adaptivePolling.pollNow,
    stopPolling: adaptivePolling.stopPolling
  };
};

export default useUnreadCountPolling;